/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Использование Redux Toolkit</h2>
    <p className={styles.description}>
      Возьмем готовую Redux-логику таймера и провередм рефакторинг используя Redux Toolkit.
    </p>
    <CopyBlock
      text={`import { createStore, combineReducers } from 'redux';

// Action types
const INCREMENT = 'timer/increment';
const DECREMENT = 'timer/decrement';

// Action creators
function increment(value) {
  return {
    type: INCREMENT,
    payload: value,
  };
}

function decrement(value) {
  return {
    type: DECREMENT,
    payload: value,
  };
}

// Reducer
function timer(state = 0, action) {
  switch (action.type) {
    case INCREMENT:
      return state + action.payload;

    case DECREMENT:
      return state - action.payload;

    default:
      return state;
  }
}

const rootReducer = combineReducers({
  timer,
});

// Store
const store = createStore(rootReducer);`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Установка</h3>
      <p className={styles.description}>Библиотека доступна как стандартный NPM-пакет. </p>
      <CopyBlock text={`npm install @reduxjs/toolkit`} language="javascript" showLineNumbers={false} theme={ocean} />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Функия configureStore()</h3>
      <p className={styles.description}>
        Обычно хранилище создается вызовом <code className={styles.code}>createStore()</code>, в который передается
        корневой редюсер. Redux Toolkit содержит функцию <code className={styles.code}>configureStore()</code>, которая
        оборачивает оригинальный <code className={styles.code}>createStore()</code>, и настраивает некоторые полезные
        инструменты разработки как часть процесса создания хранилища.
      </p>
      <a className={styles.link} href="https://redux-toolkit.js.org/api/configureStore" target="_blank">
        Документация configureStore()
      </a>
      <p className={styles.description}>
        Заменим вызов <code className={styles.code}>createStore()</code> на{' '}
        <code className={styles.code}>configureStore()</code>, который ожидает один аргумент - объект с набором строго
        именованных свойств.{' '}
      </p>
      <CopyBlock
        text={`// До
import { createStore } from 'redux';

const store = createStore(timer);

// После
import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';

const rootReducer = combineReducers({
  timer,
});

const store = configureStore({
  reducer: rootReducer,
});
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        На первый вгляд практически одно и тоже, тем не менее, после рефакторинга, под капотом сразу были настроены
        инструменты разработчика (Redux DevTools) и некоторые другие полезные функции.{' '}
      </p>
      <p className={styles.description}>
        Можно передать больше одного редюсера, и <code className={styles.code}>configureStore()</code> сам создаст
        корневой редюсер. Для этого в свойство <code className={styles.code}>reducer</code> передается объект.{' '}
      </p>
      <CopyBlock
        text={`import { configureStore } from '@reduxjs/toolkit';

const store = configureStore({
  reducer: {
    timer,
  },
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Теперь Redux-код таймера выглядит следующим образом. </p>
      <CopyBlock
        text={`import { configureStore, combineReducers } from '@reduxjs/toolkit';

// Action types
const INCREMENT = 'timer/increment';
const DECREMENT = 'timer/decrement';

// Action creators
function increment(value) {
  return {
    type: INCREMENT,
    payload: value,
  };
}

function decrement(value) {
  return {
    type: DECREMENT,
    payload: value,
  };
}

// Reducer
function timer(state = 0, action) {
  switch (action.type) {
    case INCREMENT:
      return state + action.payload;
    case DECREMENT:
      return state - action.payload;
    default:
      return state;
  }
}

// Store
const store = configureStore({
  reducer: {
    timer,
  },
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Функия createAction()</h3>
      <p className={styles.description}>
        Функция <code className={styles.code}>createAction()</code> в качестве аргумента принимает строку описывающую
        тип действия и возвращает action creator.{' '}
      </p>
      <a className={styles.link} href="https://redux-toolkit.js.org/api/createAction" target="_blank">
        Документация createAction()
      </a>
      <p className={styles.description} />
      <CopyBlock
        text={`// До
const INCREMENT = 'timer/increment';

function increment(value) {
  return {
    type: INCREMENT,
    payload: value,
  };
}
console.log(increment(5)); // {type: "timer/increment", payload: 5}

// После
import { createAction } from '@reduxjs/toolkit';

const increment = createAction('timer/increment');
console.log(increment(5)); // {type: "timer/increment", payload: 5}
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        А где взять тип действия, например для использования внутри редюсера? Есть два способа.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Метод <code className={styles.code}>toString()</code> функции <code className={styles.code}>increment</code>{' '}
          был переопределен и возвращает строку типа действия.
        </li>
        <li className={styles.list__item}>
          Значение типа действия можно получить обратившись к свойству <code className={styles.code}>type</code> функции{' '}
          <code className={styles.code}>increment</code>
        </li>
      </ul>
      <CopyBlock
        text={`const increment = createAction('INCREMENT');

console.log(increment.toString()); // "timer/increment"
console.log(increment.type); // "timer/increment"
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Продолжим рефакторить Redux-код таймера. Использование <code className={styles.code}>createAction()</code>{' '}
        избавит нас от нескольких строк кода.{' '}
      </p>
      <CopyBlock
        text={`import { configureStore, createAction } from '@reduxjs/toolkit';

// Action creators
const increment = createAction('timer/increment');
const decrement = createAction('timer/decrement');

// Reducer
function timer(state = 0, action) {
  switch (action.type) {
    case INCREMENT:
      return state + action.payload;

    case DECREMENT:
      return state - action.payload;

    default:
      return state;
  }
}

// Store
const store = configureStore({
  reducer: {
    timer,
  },
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.4 - Функия createReducer()</h3>
      <p className={styles.description}>
        Наиболее распространенным подходом создания редюсера является проверка свойства{' '}
        <code className={styles.code}>action.type</code> внутри <code className={styles.code}>switch</code> и выполнение
        определенной логики для каждого типа действия. К тому же редюсер определяет начальное значение состояния и
        возвращает полученное состояние, если он не должен обрабатывать дейтсвие такого типа.{' '}
      </p>
      <a className={styles.link} href="https://redux-toolkit.js.org/api/createReducer" target="_blank">
        Документация createReducer()
      </a>
      <p className={styles.description}>
        Используя функцию <code className={styles.code}>createReducer()</code> можно создавать редюсеры передав объект
        свойств специального формата, где каждый ключ это тип действия, а значение - это редюсер для этого типа.{' '}
      </p>
      <CopyBlock
        text={`import { createReducer } from '@reduxjs/toolkit';

const increment = createAction('timer/increment');
const decrement = createAction('timer/decrement');

const timer = createReducer(0, {
  [increment.type]: (state, action) => state + action.payload,
  [decrement.type]: (state, action) => state - action.payload,
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Так как синтаксис вычисляемых свойств объекта вызывает метод <code className={styles.code}>toString()</code>{' '}
        указанной переменной, можно просто использовать имя функции без указания свойства{' '}
        <code className={styles.code}>type</code>, ведь метод <code className={styles.code}>toString()</code> наших{' '}
        <b>action creators</b> был переопределен так, чтобы возвращать тип дейсвтвия.
      </p>
      <CopyBlock
        text={`const timer = createReducer(0, {
  [increment]: (state, action) => state + action.payload,
  [decrement]: (state, action) => state - action.payload,
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Применим этот синтаксис к коду таймера. </p>
      <CopyBlock
        text={`import { configureStore, createAction, createReducer } from '@reduxjs/toolkit';

// Action creators
const increment = createAction('timer/increment');
const decrement = createAction('timer/decrement');

// Reducer
const timer = createReducer(0, {
  [increment]: (state, action) => state + action.payload,
  [decrement]: (state, action) => state - action.payload,
});

// Store
const store = configureStore({
  reducer: {
    timer,
  },
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
